import { reportingCalendarYear } from '../../../shared/utils/reportingYear';

export const SCOPE_2_APPLICATION = Object.freeze({
  CORPORATE_AVERAGE_REFERENCE: 'CORPORATE_AVERAGE_REFERENCE',
  GREEN_NON_FOSSIL_SOLAR: 'GREEN_NON_FOSSIL_SOLAR',
});

export const SCOPE_2_GREEN_POLICY_KEY = 'SCOPE_2.POLICY::LRC_GREEN_ZERO::v1';
export const SCOPE_2_FACTOR_UNIT = 'tCO2/kWh';
export const SCOPE_2_FACTOR_CATEGORY = 'ELECTRICITY';

const asArray = value => (Array.isArray(value) ? value : []);
const asObject = value => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

export function normalizeElectricitySupplier(supplier = {}) {
  const source = asObject(supplier);
  return {
    ...source,
    id: source.id || source.supplier_ref_id || source.supplier_id || null,
    registrationNo: source.registration_no || source.registrationNo || '',
    nameJa: source.name_ja || source.supplier_name_ja || source.name || '',
    isActive: source.is_active !== false,
    recordedAt: source.updated_at || source.recorded_at || null,
    recordedBy: source.updated_by || source.recorded_by || null,
  };
}

export function normalizeElectricitySuppliers(payload) {
  const source = Array.isArray(payload)
    ? payload
    : asArray(payload?.items || payload?.suppliers);
  return source
    .map(normalizeElectricitySupplier)
    .filter(supplier => supplier.id && supplier.registrationNo);
}

export function currentOfficeAssignment(office, fallbackYear = reportingCalendarYear()) {
  const assignments = asArray(office?.electricity_assignments);
  const selectedYear = Number(office?.assignment_fiscal_year || fallbackYear);
  const matching = assignments
    .filter((assignment) => {
      const effectiveFrom = Number(
        assignment?.effective_from_fiscal_year || assignment?.fiscal_year,
      );
      const effectiveTo = Number(assignment?.effective_to_fiscal_year || 0);
      return effectiveFrom <= selectedYear && (!effectiveTo || selectedYear <= effectiveTo);
    })
    .sort((left, right) => Number(
      right?.effective_from_fiscal_year || right?.fiscal_year,
    ) - Number(left?.effective_from_fiscal_year || left?.fiscal_year))[0];
  return matching
    || office?.electricity_assignment
    || office?.current_electricity_assignment
    || null;
}

export function normalizeOfficeAssignment(office, fallbackYear) {
  const source = asObject(currentOfficeAssignment(office, fallbackYear));
  const selectedFiscalYear = Number(
    office?.assignment_fiscal_year || fallbackYear || reportingCalendarYear(),
  );
  const supplierId = source.supplier_ref_id
    || source.supplier_id
    || source.supplier?.id
    || '';
  const applicationCode = source.application_code
    || SCOPE_2_APPLICATION.CORPORATE_AVERAGE_REFERENCE;
  return {
    id: source.id || source.assignment_id || null,
    isConfigured: Boolean(source.id || supplierId),
    fiscalYear: selectedFiscalYear,
    effectiveFromFiscalYear: Number(
      source.effective_from_fiscal_year || source.fiscal_year || selectedFiscalYear,
    ),
    effectiveToFiscalYear: Number(source.effective_to_fiscal_year) || null,
    supplierId,
    supplierName: source.supplier_name_ja
      || source.supplier?.name_ja
      || '',
    registrationNo: source.supplier_registration_no
      || source.registration_no
      || source.supplier?.registration_no
      || '',
    applicationCode,
    isGreen: applicationCode === SCOPE_2_APPLICATION.GREEN_NON_FOSSIL_SOLAR,
    recordedAt: source.updated_at
      || source.recorded_at
      || source.approved_at
      || source.provenance?.recorded_at
      || source.provenance?.approved_at
      || null,
    recordedBy: source.updated_by
      || source.recorded_by
      || source.approved_by
      || source.provenance?.recorded_by
      || source.provenance?.approved_by
      || null,
    derivedItemKey: source.ef_item_key || source.derived?.ef_item_key || '',
    version: Number(source.entity_version || source.version || source.assignment_version) || null,
    impact: source.impact || source.recalculation_impact || null,
  };
}

export function toAssignmentForm(office) {
  const assignment = normalizeOfficeAssignment(office);
  return {
    effective_from_fiscal_year: String(assignment.effectiveFromFiscalYear),
    supplier_id: assignment.supplierId,
    application_code: assignment.applicationCode,
  };
}

export function hasAssignmentMappingChange(values, currentAssignment) {
  if (!currentAssignment?.isConfigured) return true;
  return Number(values.effective_from_fiscal_year)
      !== currentAssignment.effectiveFromFiscalYear
    || values.supplier_id !== currentAssignment.supplierId
    || values.application_code !== currentAssignment.applicationCode;
}

export function toAssignmentPayload(values, currentAssignment) {
  if (!hasAssignmentMappingChange(values, currentAssignment)) return null;
  return {
    effective_from_fiscal_year: Number(values.effective_from_fiscal_year),
    supplier_id: values.supplier_id,
    application_code: values.application_code,
  };
}

export function derivedScope2ItemKey(supplier, applicationCode) {
  if (applicationCode === SCOPE_2_APPLICATION.GREEN_NON_FOSSIL_SOLAR) {
    return SCOPE_2_GREEN_POLICY_KEY;
  }
  const registrationNo = normalizeElectricitySupplier(supplier).registrationNo;
  if (!registrationNo) return '';
  return `SCOPE_2.RETAILER::${registrationNo}::CORPORATE_AVERAGE_REFERENCE`;
}
